import React, { useCallback, useEffect, useRef, useState } from "react";
import { Activity, TrendingDown, TrendingUp, Zap } from "lucide-react";
import api from "../../lib/api";
import { fmt, fmtInt, relativeTime } from "../../lib/format";

const LIVE_POLL_MS = 30_000;

function Stat({ label, value, unit, accent, Icon, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        <span className="label-tiny">{label}</span>
      </div>
      <div className="font-heading text-2xl font-semibold tabular-nums" style={{ color: accent }}>
        {value}
        {unit && <span className="text-xs text-muted-foreground ml-1 font-normal">{unit}</span>}
      </div>
    </div>
  );
}

/**
 * Live energy card — polls /energy/live every 30s and shows the current
 * household draw plus today's import/export deltas. Renders gracefully
 * when no bridge has connected yet ("—" everywhere, helpful empty state).
 */
export default function LivePowerCard({ siteId }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const timerRef = useRef(null);

  const reload = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/sites/${siteId}/energy/live`);
      setSnap(data);
      setErr(null);
    } catch (e) {
      setErr(e.userMessage || "Could not load live energy");
    }
  }, [siteId]);

  useEffect(() => {
    reload();
    timerRef.current = setInterval(reload, LIVE_POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [reload]);

  const hasData = snap && snap.current_power_w != null;
  const power = snap?.current_power_w;
  const isExporting = typeof power === "number" && power < 0;
  const powerAccent = isExporting ? "#10B981" : "#F59E0B";
  // Show absolute power; the direction is conveyed by the colour + sub-label.
  const powerDisplay = power == null ? "—" : fmtInt(Math.abs(power));

  return (
    <section className="card-base p-5" data-testid="live-power-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-[#F59E0B]" />
          <h2 className="label-tiny">Right now</h2>
          {hasData && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#10B981]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
              Live
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {snap?.last_seen_at ? `updated ${relativeTime(snap.last_seen_at)}` : "no bridge data yet"}
        </div>
      </div>

      {err ? (
        <div className="text-xs text-[#EF4444]" data-testid="live-power-error">{err}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Stat
            label={isExporting ? "Exporting now" : "Drawing now"}
            value={powerDisplay}
            unit="W"
            accent={powerAccent}
            Icon={Activity}
            testid="live-current-power"
          />
          <Stat
            label="Today's import"
            value={fmt(snap?.today_import_kwh, 2)}
            unit="kWh"
            accent="#E11D48"
            Icon={TrendingDown}
            testid="live-today-import"
          />
          <Stat
            label="Today's injection"
            value={fmt(snap?.today_export_kwh, 2)}
            unit="kWh"
            accent="#10B981"
            Icon={TrendingUp}
            testid="live-today-export"
          />
          <Stat
            label="Peak today"
            value={snap?.peak_power_today_w == null ? "—" : fmtInt(snap.peak_power_today_w)}
            unit="W"
            accent="#F59E0B"
            Icon={Activity}
            testid="live-peak-today"
          />
        </div>
      )}
      {!hasData && !err && (
        <p className="text-[11px] text-muted-foreground mt-4" data-testid="live-empty-hint">
          Run the HomeWizard bridge (Settings → Bridge token) to start streaming live metrics here.
        </p>
      )}
    </section>
  );
}
